/**
 * THE CONSOLE'S HALF OF THE INSTALL PROFILE (TEC-07).
 *
 * The console is ONE build served to Moonbase's own fleet and to every customer
 * box. Its Platform group offered Fleet & Estate, Marketplace and Environments
 * unconditionally, and `estate.ts` queries `controlplane.*` — a schema a customer
 * deployment must never have. Nothing broke loudly: the pages just returned
 * PostgREST's "schema not exposed" error, so the customer's console advertised
 * Moonbase's fleet management as a feature of their box and then failed to open it.
 *
 * That is the same trust boundary as the CLI's `admitsModule`, drawn a fourth
 * time — see `packages/installer/src/profile.ts` for why that is the finding
 * rather than an implementation detail. The console cannot import the installer's
 * node-side predicate (it reads the filesystem), so what it shares is the RULE and
 * the DEFAULT: internal surfaces are off unless this deployment demonstrably has
 * the control plane, and 'customer' is what you get by omission.
 *
 * Two independent signals, deliberately OR-ed rather than either alone:
 *   · the build profile — `VITE_SOLUM_PROFILE=internal`, set by Moonbase's own
 *     deployment. Needed because the demo/fixture console has no live ledger to
 *     read and would otherwise never show the surfaces it exists to show.
 *   · the live registry — `controlplane` present in `core.module`. Needed because
 *     the profile is a build-time claim and the ledger is the fact.
 */

import { runtimeEnv } from '@solum/auth/runtime-config'
/** Who this console instance is for. Mirrors `@solum/installer`'s InstallProfile. */
export type ConsoleProfile = 'customer' | 'internal'

/** The module whose presence IS the control plane. */
export const CONTROLPLANE = 'controlplane'

/** The build's declared profile, defaulting closed. */
export function consoleProfile(env: Record<string, string | undefined> = { VITE_SOLUM_PROFILE: runtimeEnv('VITE_SOLUM_PROFILE') }): ConsoleProfile {
  return env.VITE_SOLUM_PROFILE === 'internal' ? 'internal' : 'customer'
}

/**
 * May this console show the fleet surfaces (Estate · Marketplace · Environments)?
 *
 * Pure and injectable so the decision is unit-testable without rendering — the
 * console has no DOM test environment, and a gate that can only be verified by
 * looking at it is a gate nobody re-verifies.
 */
export function showsFleetSurfaces(
  profile: ConsoleProfile,
  isInstalled: (key: string) => boolean,
): boolean {
  return profile === 'internal' || isInstalled(CONTROLPLANE)
}

/** What to tell someone who deep-links to a fleet route on a customer box. This
 *  is a REFUSAL, not an error: the route is fine, the box is simply not the
 *  control plane, and saying so beats a raw PostgREST schema-not-exposed dump. */
export const FLEET_UNAVAILABLE =
  'Fleet management runs on the Moonbase control plane, not on this deployment. ' +
  'This box holds only its own data — by design.'
